import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, MailWarning, MailCheck, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { API, api, fmtPrice, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/lib/content";

const RESEND_COOLDOWN_SECONDS = 60;

function useResendVerification() {
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      await api.post("/auth/resend-verification", { origin_url: window.location.origin });
      toast.success("Verification email sent", {
        description: "Check your inbox — and your Junk or Spam folder if you don't see it.",
      });
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return { resend, sending, cooldown };
}

function CheckEmail({ email }) {
  const { resend, sending, cooldown } = useResendVerification();
  return (
    <div data-testid="check-email-screen" className="mx-auto max-w-lg">
      <div className="border border-white/10 bg-[#121418] p-8 sm:p-10">
        <MailCheck className="h-10 w-10 text-[#FF5500]" />
        <h2 className="font-display mt-5 text-2xl font-bold uppercase tracking-tight text-slate-100">
          Check your email
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-slate-300">
          We've sent a verification link to:
        </p>
        <p data-testid="check-email-address" className="mt-1 text-sm font-semibold text-[#FF5500]">
          {email}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-slate-400">
          It can take a few minutes to arrive.
        </p>
        <div className="mt-6 border-t border-white/10 pt-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            If you don't see it:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-slate-300">
            <li className="flex items-start gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#FF5500]" />Check your Junk or Spam folder</li>
            <li className="flex items-start gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#FF5500]" />Search your inbox for "Sitebound"</li>
            <li className="flex items-start gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#FF5500]" />Make sure you entered the correct email address</li>
            <li className="flex items-start gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#FF5500]" />Wait a few minutes before requesting another email</li>
          </ul>
        </div>
        <button
          data-testid="check-email-resend-button"
          onClick={resend}
          disabled={sending || cooldown > 0}
          className="mt-8 w-full border border-[#FF5500] px-5 py-3 text-xs font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white disabled:opacity-50"
        >
          {sending ? "Sending…" : cooldown > 0 ? `Resend available in ${cooldown}s` : "Resend verification email"}
        </button>
        {cooldown > 0 && (
          <p data-testid="resend-cooldown-note" className="mt-3 text-center text-xs text-slate-500">
            You can request another email after the current resend cooldown. Still haven't received
            it? Check your Junk or Spam folder before requesting another email.
          </p>
        )}
      </div>
    </div>
  );
}

function AuthForm({ onRegistered }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
        setNotice("If an account exists for that email, a reset link is on its way. Check your inbox (and junk folder) — the link expires in 30 minutes.");
      } else if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register(name, email, password);
        onRegistered?.();
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (mode === "forgot") {
    return (
      <div className="mx-auto max-w-md">
        <h2 className="font-display mb-2 text-2xl font-bold uppercase tracking-tight text-slate-100">
          Reset your password
        </h2>
        <p className="mb-8 text-sm text-slate-400">
          Enter your account email and we'll send you a reset link.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mono-label mb-2 block" htmlFor="forgot-email">Email</label>
            <Input
              id="forgot-email"
              data-testid="forgot-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-white/15 bg-[#0A0B0D] text-slate-100"
            />
          </div>
          {notice && (
            <p data-testid="forgot-notice" className="border border-[#FF5500]/30 bg-[#FF5500]/10 px-3 py-2 text-sm text-slate-300">
              {notice}
            </p>
          )}
          <button
            data-testid="forgot-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#FF5500] px-5 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800] disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send reset link"}
          </button>
          <p className="text-center text-xs text-slate-500">
            <button
              type="button"
              data-testid="forgot-back-to-login"
              onClick={() => { setMode("login"); setNotice(""); }}
              className="text-[#FF5500] underline underline-offset-2"
            >
              Back to sign in
            </button>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-8 grid grid-cols-2 border border-white/15">
        {["login", "register"].map((m) => (
          <button
            key={m}
            data-testid={`auth-tab-${m}`}
            onClick={() => {
              setMode(m);
              setError("");
            }}
            className={`py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
              mode === m ? "bg-[#FF5500] text-white" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {m === "login" ? "Sign in" : "Create account"}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <div>
            <label className="mono-label mb-2 block" htmlFor="auth-name">Name</label>
            <Input
              id="auth-name"
              data-testid="auth-name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-white/15 bg-[#0A0B0D] text-slate-100"
            />
          </div>
        )}
        <div>
          <label className="mono-label mb-2 block" htmlFor="auth-email">Email</label>
          <Input
            id="auth-email"
            data-testid="auth-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border-white/15 bg-[#0A0B0D] text-slate-100"
          />
        </div>
        <div>
          <label className="mono-label mb-2 block" htmlFor="auth-password">
            Password {mode === "register" && <span className="text-slate-500 normal-case">(min 8 characters)</span>}
          </label>
          <Input
            id="auth-password"
            data-testid="auth-password-input"
            type="password"
            required
            minLength={mode === "register" ? 8 : 1}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border-white/15 bg-[#0A0B0D] text-slate-100"
          />
        </div>
        {error && (
          <p data-testid="auth-error" className="border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
            {error}
          </p>
        )}
        <button
          data-testid="auth-submit-button"
          type="submit"
          disabled={loading}
          className="w-full bg-[#FF5500] px-5 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800] disabled:opacity-60"
        >
          {loading ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
        <p className="text-center text-xs text-slate-500">
          {mode === "login" ? (
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => setMode("forgot")}
              className="text-[#FF5500] underline underline-offset-2"
            >
              Forgot password?
            </button>
          ) : (
            "Just bought as a guest? Create an account with the same email, verify it, and your purchases will appear here."
          )}
        </p>
      </form>
    </div>
  );
}

function Dashboard({ user, logout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { resend, sending: resending, cooldown } = useResendVerification();

  useEffect(() => {
    api
      .get("/orders/mine")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const verified = user.email_verified !== false;
  const orders = data?.orders || [];

  return (
    <div data-testid="account-dashboard" className="mx-auto max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-slate-100">
            G'day, {user.name}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{user.email}</p>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-300 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>

      {!verified && (
        <div
          data-testid="verify-banner"
          className="mt-8 border border-[#FF5500]/40 bg-[#FF5500]/10 p-5"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <MailWarning className="mt-0.5 h-5 w-5 shrink-0 text-[#FF5500]" />
              <div>
                <p className="text-sm font-semibold text-slate-100">Verify your email to see your purchases</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-400">
                  We sent a verification link to {user.email}. It can take a few minutes to arrive —
                  check your Junk or Spam folder, or search your inbox for "Sitebound". Verifying
                  confirms ownership before any guest purchases made with this email are linked to
                  your account.
                </p>
              </div>
            </div>
            <button
              data-testid="resend-verification-button"
              onClick={resend}
              disabled={resending || cooldown > 0}
              className="border border-[#FF5500] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white disabled:opacity-50"
            >
              {resending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
            </button>
          </div>
          {cooldown > 0 && (
            <p data-testid="banner-cooldown-note" className="mt-3 text-xs text-slate-500">
              You can request another email after the current resend cooldown. Check your Junk or
              Spam folder before requesting another email.
            </p>
          )}
        </div>
      )}

      <div className="mono-label mt-12">Your packs</div>
      {loading ? (
        <p className="mt-6 text-sm text-slate-500">Loading your orders…</p>
      ) : !verified ? (
        <p data-testid="orders-locked" className="mt-6 border border-white/10 bg-[#121418] p-10 text-center text-sm text-slate-400">
          Purchase history unlocks once your email is verified.
        </p>
      ) : orders.length === 0 ? (
        <div data-testid="orders-empty" className="mt-6 border border-white/10 bg-[#121418] p-10 text-center">
          <PackageOpen className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-4 text-sm text-slate-400">No purchases yet.</p>
          <Link
            data-testid="orders-empty-cta"
            to="/product/complete-construction-job-seeker-pack"
            className="mt-6 inline-block bg-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E04800]"
          >
            Get the Complete Pack
          </Link>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {orders.map((o) => (
            <div
              key={o.order_id}
              data-testid={`order-row-${o.order_id}`}
              className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-[#121418] p-5"
            >
              <div>
                <div className="font-mono-ind text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {o.order_id} · {o.paid_at ? new Date(o.paid_at).toLocaleDateString("en-AU") : ""}
                </div>
                <div className="mt-1.5 font-semibold text-slate-100">{o.product_name}</div>
                <div className="mt-0.5 text-xs text-slate-500">{fmtPrice(o.amount)}</div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  data-testid={`order-hub-${o.order_id}`}
                  to="/hub"
                  className="flex items-center gap-2 bg-[#FF5500] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
                >
                  Job Seeker Hub
                </Link>
                <a
                  data-testid={`order-download-${o.order_id}`}
                  href={`${API}/downloads/${o.download_token}`}
                  className="flex items-center gap-2 border border-[#FF5500] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white"
                >
                  <Download className="h-3.5 w-3.5" /> Download ZIP
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  usePageTitle("My Account — Sitebound", "Sign in to your Sitebound account to view purchases and re-download your packs.");
  const { user, logout } = useAuth();
  const [justRegistered, setJustRegistered] = useState(false);
  const unverified = user && user.email_verified === false;

  return (
    <div data-testid="account-page" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <div className="mono-label">Account</div>
      <h1 className="font-display mt-4 mb-12 text-5xl font-extrabold uppercase tracking-tight text-slate-100">
        {user ? "Your Sitebound account" : "Sign in"}
      </h1>
      {user === null ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : user === false ? (
        <AuthForm onRegistered={() => setJustRegistered(true)} />
      ) : justRegistered && unverified ? (
        <CheckEmail email={user.email} />
      ) : (
        <Dashboard user={user} logout={logout} />
      )}
    </div>
  );
}
